import React from 'react';
import {Button, ButtonProps, CircularProgress, withStyles} from '@material-ui/core';
import {useTranslation} from 'react-i18next';
import {compose} from 'redux';
import {Save as SaveIcon} from '@material-ui/icons';
import classnames from "classnames";

interface SaveButtonProps extends ButtonProps {
    loading?: boolean;
    className?: string;
    handleSubmitWithRedirect: Function;
    submitOnEnter?: boolean;
    saveButtonLabel?: string;
    saveButtonIcon?: React.ComponentType;
    noIcon?: boolean;
    iconProps?: Record<string, any>;
}

export interface SaveButtonPropsShape extends ButtonProps {
    handleSubmitWithRedirect?: Function;
    loading?: boolean;
    submitOnEnter?: boolean;
    className?: string;
    saveButtonLabel?: string;
    saveButtonIcon?: React.ComponentType;
    noIcon?: boolean;
}

interface InternalSaveButtonProps extends SaveButtonProps {
    classes: Record<string, string>;
}

interface ButtonIconProps {
    noIcon: boolean;
    loading: boolean;
    classes: Record<string, string>;
    SaveButtonIcon: React.ComponentType<any>;
    iconProps?: Record<string, any>;
}

const ButtonIcon: React.FunctionComponent<ButtonIconProps> = ({
    noIcon,
    loading,
    classes,
    SaveButtonIcon,
    iconProps= {},
}) => {
    if (noIcon) {
        return null;
    }

    if (loading) {
        return (
            <CircularProgress
                className={classes.loaderIcon}
                color="inherit"
                size={20}
            />
        );
    }

    return (
        <SaveButtonIcon
            className={classes.icon}
            {...iconProps}
        />
    );
};

const SaveButtonView: React.FunctionComponent<InternalSaveButtonProps> = ({
    handleSubmitWithRedirect,
    loading = false,
    disabled= false,
    submitOnEnter,
    className,
    saveButtonLabel = 'common.actions.save',
    saveButtonIcon: SaveButtonIcon,
    classes,
    onClick,
    noIcon = false,
    iconProps,
    ...rest
}) => {
    const {t: translate} = useTranslation();
    const saveButtonType = submitOnEnter ? 'submit' : 'button';
    const resultNoIcon = noIcon || SaveButtonIcon === null;
    const Icon = SaveButtonIcon || SaveIcon;

    return (
        <Button
            onClick={(e): void => {
                e && e.preventDefault();
                if (loading) {
                    return;
                }

                handleSubmitWithRedirect();

                if (typeof onClick === 'function') {
                    onClick(e);
                }
            }}
            variant="contained"
            size="medium"
            className={classnames(`text-white ${className}`, classes.saveButton)}
            color="primary"
            disabled={loading || disabled}
            type={saveButtonType}
            {...rest}
        >
            <div className={classes.iconContainer}>
                <ButtonIcon
                    SaveButtonIcon={Icon}
                    loading={loading}
                    noIcon={resultNoIcon}
                    classes={classes}
                    iconProps={iconProps}
                />
            </div>

            {translate(saveButtonLabel)}
        </Button>
    );
};

const SaveButton: React.ComponentType<SaveButtonProps> = compose(
    withStyles({
        iconContainer: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
        },
        saveButton: {
            textTransform: 'capitalize',
        },
        icon: {
            fontSize: '22px',
            marginRight: '6px'
        },
        loaderIcon: {
            fontSize: '22px',
            marginRight: '6px'
        },
    }),
)(SaveButtonView);

export default SaveButton;